/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, useEffect, useCallback } from "react";

//@Packages
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Button,
  EmptyState,
  StatusBadge, Table, TableContent, TableHeader, useTableSkeleton
} from "@forge/common";

//@Components
import NoDevice from "../nodevice";
import CustomizedTable from "./customizedtable";
import CustomizeColumns from "./customizecolumns";

//@Models
import FilterModal from "./FilterModal/FilterModal";
import { LinkedDeviceListHeaderProps, LinkedDeviceListProps } from "../../models/linkedDeviceList";

//@Services
import { useGetDownstreamDeviceListQuery } from "../../services/api";

//@Static Data
import { extractAllEbiNameplates, extractAllNiagaraNameplates, gatewayErrorStatusMessages, getDynamicFilterData, getDynamicHeaderData, getDynamicInterfaceData } from "../../utils/utility";

//@Constants
import { GatewayStatus, LinkedDeviceStatus, LOCALE_CONST } from "../../utils/constants";

//@Styles
import "./styles.scss";

interface DownstreamDeviceListProps {
  gatewayType: string;
}

//@Functional component
const LinkedDeviceList: React.FC<DownstreamDeviceListProps> = ({ gatewayType }) => {
  const flags = useFlags();
  const { t } = useTranslation();

  const [isSearch, setIsSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterMetaData, setFilterMetaData] = useState(null);
  const [isCustomizeColumnsOpen, setIsCustomizeColumnsOpen] = useState(false);
  const interfaceDecider = getDynamicInterfaceData(gatewayType);
  const [deviceLists, setDeviceLists] = useState<ReturnType<typeof interfaceDecider>[]>([]);
  const [errorData, setErrorData] = useState<any>([]);

  const convertFn = (headers: LinkedDeviceListHeaderProps[]) => {
    console.log("Headers:", headers);
    return headers.map((header: LinkedDeviceListHeaderProps) => {
      return { ...header, content: t(header.content) };
    });
  };

  const convertStrFn = (value: string) => t(value);

  const [tableHeaders, settableHeaders] =
    useState<LinkedDeviceListHeaderProps[]>(convertFn(getDynamicHeaderData(gatewayType, flags)));
  const [filterHeaders, setFilterHeaders] =
    useState<LinkedDeviceListHeaderProps[]>(convertFn(getDynamicHeaderData(gatewayType, flags)));

  const getDeviceID = () => sessionStorage.getItem("linkedDevicesSystemGuid") || "";

  const {
    data: deviceListData,
    isError: deviceListError,
    error: deviceListErrorData,
    isLoading: deviceListLoading,
    isSuccess: deviceListSuccess,
    isFetching: deviceListFetching,
    refetch: refetchDownstreamDeviceList
  } = useGetDownstreamDeviceListQuery({
    deviceId: getDeviceID()
  });

  //const checkWhichGateway = gatewayType === "EBI" ? extractAllEbiNameplates(deviceListData) : extractAllNiagaraNameplates();
  const checkWhichGateway = (gatewayType: string, deviceListData: any) => {
    if (gatewayType === "EBI") {
      return extractAllEbiNameplates(deviceListData);
    } else if (gatewayType === "honeywell-niagara-device" || gatewayType === "honeywell-bms-gateway" || gatewayType === "alerton-compass") {
      return extractAllNiagaraNameplates(deviceListData);
    }
    else {
      return [];
    }

  }
  useEffect(() => {
    if (deviceListSuccess) {
      setDeviceLists(checkWhichGateway(gatewayType, deviceListData) || []);
    } else if (deviceListError) {
      setErrorData(deviceListErrorData);
      setDeviceLists([]);
    } else {
      //do nothing
    }
  }, [deviceListData, deviceListError, deviceListErrorData, deviceListSuccess]);

  useEffect(() => {
    const getFilterData = tableHeaders.filter(
      (item: LinkedDeviceListHeaderProps) => item.ischecked
    );

    setFilterHeaders(getFilterData);
  }, [tableHeaders]);

  useEffect(() => {
    refetchDownstreamDeviceList();
  }, []);

  const formatContentData = (content: LinkedDeviceListProps[]) => {
    const getColorClsName = (status: string = "") => {
      if (status === LinkedDeviceStatus.Connected) {
        return "green";
      } else if (status === LinkedDeviceStatus.Unstable) {
        return "orange";
      } else {
        return "gray";
      }
    };

    const formatCellContent = (value: any) => value || "-";

    const displayStatus = (status: string) => {
      if (status === LinkedDeviceStatus.Connected) {
        return t(LOCALE_CONST.connected);
      } else if (status === LinkedDeviceStatus.Unstable) {
        return t(LOCALE_CONST.unstable);
      } else if (status === LinkedDeviceStatus.Disconnected) {
        return t(LOCALE_CONST.disconnected);
      } else {
        return "-";
      }
    };

    const statusClsName = (status: string) => {
      let clsName = "";

      if (status === GatewayStatus.Error) {
        clsName = "flex items-center";
      }

      return clsName;
    };

    return content.map((item: LinkedDeviceListProps, index: number) => {
      return {
        id: (index + 1).toString(),
        cell: filterHeaders?.map((header) => {
          let cellContent = <span>{formatCellContent(item[header.id])}</span>;

          if (header.id === "status") {
            cellContent = (
              <span className={statusClsName(item.status)}>
                {item.status && (
                  <StatusBadge className={`mr-1 text-[0.5rem]`}
                    color={getColorClsName(item.status)}
                  />
                )}
                {displayStatus(item.status)}
              </span>
            );
          }

          return { content: cellContent };
        })
      };
    });
  };

  const filterIconClick = () => setIsFilterOpen(true);
  const showHideIconClick = () => setIsCustomizeColumnsOpen(true);

  const tableSkeleton = useTableSkeleton(10, 7);

  // For Search
  const normalizeSpaces = (str: string) => str.toLowerCase().replace(/\s+/g, ' ').trim();

  const mapStatus = (status: string | null) => {
    if (!status) return "";
    switch (status.toLowerCase()) {
      case "online":
        return "connected";
      case "offline":
        return "disconnected";
      case "marginal":
        return "unstable";
      default:
        return status.toLowerCase();
    }
  };

  const searchOnChange = (value: string) => {
    if (value === "") {
      setTimeout(() => {
        setIsSearch(false);
        setDeviceLists(checkWhichGateway(gatewayType, deviceListData));
      }, 1000);
      debouncedSearchTextFetch(normalizeSpaces(value));
    } else {
      setIsSearch(true);
      if (value.split(" ").join("").length >= 3)
        debouncedSearchTextFetch(normalizeSpaces(value));
    }
    setSearchValue(value);
  };

  const debouncedSearchTextFetch = useCallback(
    debounce((text: string) => {
      const normalizedText = text.toLowerCase().trim();
      const statusMapping: { [key: string]: string } = {
        connected: "online",
        disconnected: "offline",
        unstable: "marginal"
      };

      const mappedStatus = Object.keys(statusMapping).find(
        key => statusMapping[key] === normalizedText
      ) || normalizedText;

      const filteredData = deviceLists?.filter((device) => {
        const deviceStatus = mapStatus(device.status).toLowerCase();

        // Check if any of the keys match the normalizedText
        const matchesText = getDynamicFilterData(gatewayType, flags).some((key: string) => {
          return device[key]?.toLowerCase().includes(normalizedText);
        });

        // Check if the status matches the mappedStatus
        const matchesStatus = deviceStatus.includes(mappedStatus);


        return matchesText || matchesStatus;
      });

      setDeviceLists(filteredData);
    }, 500),
    [deviceLists]
  );

  const onSearchClear = () => {
    setTimeout(() => {
      setIsSearch(false);
      setDeviceLists(checkWhichGateway(gatewayType, deviceListData));
    }, 1000);
    setSearchValue("");
    debouncedSearchTextFetch("");
  };

  const SCROLLABLE_THRESHOLD = 15;

  // Filter function based on rules
  function filterArray(array: any, filters: any) {
    const filterKeys = Object.keys(filters);

    // If no filters are selected, return the original array
    const noFiltersSelected = filterKeys.every(key => filters[key].length === 0);
    if (noFiltersSelected) return array;

    return array.filter((item: any) => {
      // Check if all filter criteria are met
      const matchesAllFilters = filterKeys.every(key => {
        // If the filter is not empty, check if the item's property matches any of the filter values
        if (filters[key].length > 0) {
          return filters[key].includes(item[key]);
        }
        // If no filter is selected for this key, consider it a match
        return true;
      });

      return matchesAllFilters;
    });
  }

  const onApplyFilters = (filteredData: any) => {
    setFilterMetaData(filteredData);
    if (Object.keys(filteredData).length === 0) {
      clearFilters();
    } else {
      setDeviceLists(filterArray(checkWhichGateway(gatewayType, deviceListData), filteredData));
    }
  };

  const clearFilters = () => {
    setFilterMetaData(null);
    setDeviceLists(checkWhichGateway(gatewayType, deviceListData));
  };

  const areFiltersSelected = () => {
    if (!filterMetaData) return false;

    return Object.keys(filterMetaData).some((key) => {
      return (filterMetaData[key]?.length || 0) > 0;
    });
  };

  const exportCSV = (exportLists: string[][]) => {
    let content = exportLists.map(row => row.join(",")).join("\n").replace(/\r/g, '');

    const blobContent = new Blob([content], { type: "text/csv" });

    const link = document.createElement("a");

    link.href = URL.createObjectURL(blobContent);
    link.download = `${getDeviceID()}-linked-device.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onExportAction = () => {
    const exportLists: string[][] = [];
    const headerExportLists: string[] = [];

    deviceLists.forEach((item: LinkedDeviceListProps, index: number) => {
      const exportItem: string[] = [];

      tableHeaders.forEach((header: LinkedDeviceListHeaderProps) => {
        if (index === 0) {
          headerExportLists.push(header.content || "");
        }

        exportItem.push(item[header.id] || "");
      });

      exportLists.push(exportItem);
    });

    exportLists.unshift(headerExportLists);

    exportCSV(exportLists);
  };

  const renderListComp = () => {
    if (deviceListLoading || deviceListFetching) {
      return (
        <Table>
          <TableHeader data={filterHeaders} />
          <TableContent data={tableSkeleton} />
        </Table>
      );
    } else if (deviceListError) {
      return (
        <EmptyState variant="4xx"
          title={gatewayErrorStatusMessages(convertStrFn, `${t(LOCALE_CONST.somethingWentWrong)}...`, errorData?.status, { 
            404: t(LOCALE_CONST.clientError_404)
          }) }
          description={`${t(LOCALE_CONST.tryReloading)}. (${t(LOCALE_CONST.errCode, { errorCode: errorData?.status || t(LOCALE_CONST.error) })})`}
        >
          <Button className="mx-auto block" onClick={refetchDownstreamDeviceList}>{t(LOCALE_CONST.reloadPage)}</Button>
        </EmptyState>
      );
    } else if (deviceLists.length <= 0 && !isSearch && !areFiltersSelected()) {
      return <NoDevice titleKey={t(LOCALE_CONST.linkedDevices)} h2Text={t(LOCALE_CONST.noDevicesFound)} h3Text="" />;
    } else {
      let clsName = "";

      if (deviceLists.length > SCROLLABLE_THRESHOLD) {
        clsName = "scrollable-container";
      }

      return (
        <div data-testid="linked-devices-list-container" className={clsName}>
          <CustomizedTable title={t(LOCALE_CONST.linkedDevices)}
            tableHeaderData={filterHeaders} isSearch={isSearch}
            tableData={deviceLists} searchValue={searchValue}
            tableContentData={formatContentData(deviceLists)}
            searchOnChange={searchOnChange} onSearchClear={onSearchClear}
            showHideIconClick={showHideIconClick} filterIconClick={filterIconClick}
            onExportAction={onExportAction}
          />
        </div>
      );
    }
  };

  return (
    <>
      {renderListComp()}
      {isCustomizeColumnsOpen && (
        <CustomizeColumns tableHeaders={tableHeaders}
          initialTableHeaders={convertFn(getDynamicHeaderData(gatewayType, flags))}
          setClose={setIsCustomizeColumnsOpen} settableHeaders={settableHeaders}
        />
      )}
      {isFilterOpen && (
        <FilterModal closeModal={setIsFilterOpen}
          onApplyFilters={onApplyFilters} clearFilters={clearFilters}
          filterMetaData={filterMetaData} data={checkWhichGateway(gatewayType, deviceListData) || []}
          gatewayType={gatewayType}
          flags={flags}
        />
      )}

    </>
  );
};

export default LinkedDeviceList;